import { useState, useEffect, useCallback } from 'react';

const MAX_HISTORY = 5;

/**
 * Track the last N focused kurals in-memory (per page load).
 * Not persisted; cleared on reload.
 *
 * Pushes automatically whenever ``current`` changes to a value that
 * differs from the tail. Provides ``pop`` to rewind to an earlier crumb.
 */
export function useFocusHistory(current: number | null): {
  history: number[];
  jumpTo: (index: number) => void;
} {
  const [history, setHistory] = useState<number[]>([]);

  useEffect(() => {
    if (current == null) return;
    setHistory((h) => {
      if (h[h.length - 1] === current) return h;
      // If current appears earlier in history, rewind to that point.
      const existingIdx = h.indexOf(current);
      if (existingIdx >= 0) {
        return h.slice(0, existingIdx + 1);
      }
      const next = [...h, current];
      return next.length > MAX_HISTORY ? next.slice(-MAX_HISTORY) : next;
    });
  }, [current]);

  const jumpTo = useCallback((index: number) => {
    setHistory((h) => h.slice(0, index + 1));
  }, []);

  return { history, jumpTo };
}
